import { isNumber } from 'lodash';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { limitsUpdatesHistoryApi } from 'api/limits-updates-history.api';
import {
  CopyTextId,
  CrudPage,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { usePartialQuery, useQueryFilters } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, LimitsUpdatesHistoryEntity } from 'types';

export const LimitsUpdatesHistory: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.limits_updates_history',
  });
  const { t: tCommon } = useTranslation();

  const queryResult = usePartialQuery(
    QueryKey.LimitsUpdatesHistory,
    limitsUpdatesHistoryApi.getAllPaginated,
  );

  useQueryFilters<any>({
    from: moment().subtract(1, 'w').startOf('d').toISOString(),
  });

  const renderNumber = useCallback((value: number, prefix?: string) => {
    if (!isNumber(value)) {
      return null;
    }
    return (
      <div>
        {(prefix ? `${prefix}: ` : '') + (value > 0 ? `+${value}` : value)}
      </div>
    );
  }, []);

  const columns = useMemo(
    (): DataGridColumnDefinition<LimitsUpdatesHistoryEntity>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('columns.requisites'),
        valueGetter: (item) => <CopyTextId id={item.requisitesId} />,
      },
      {
        header: t('columns.order'),
        valueGetter: (item) => <CopyTextId id={item.orderId} />,
      },
      {
        header: t('columns.sum'),
        valueGetter: (item) => (
          <Fragment>
            {renderNumber(item.limitSumPerHour, t('fields.hour'))}
            {renderNumber(item.limitSumPerDay, t('fields.day'))}
            {renderNumber(item.limitSumPerMonth, t('fields.month'))}
          </Fragment>
        ),
      },
      {
        header: t('columns.amount'),
        valueGetter: (item) => (
          <Fragment>
            {renderNumber(item.limitCountPerHour, t('fields.hour'))}
            {renderNumber(item.limitCountPerDay, t('fields.day'))}
            {renderNumber(item.limitCountPerMonth, t('fields.month'))}
          </Fragment>
        ),
      },
      {
        header: t('columns.active_orders'),
        valueGetter: (item) => renderNumber(item.limitActiveOrders),
      },
      {
        header: t('columns.consecutive'),
        valueGetter: (item) => (
          <Fragment>
            {renderNumber(item.consecutiveCancellations, t('fields.cancel'))}
            {renderNumber(item.consecutiveDisputes, t('fields.dispute'))}
            {renderNumber(
              item.consecutiveManualComplete,
              t('fields.manual_complete'),
            )}
          </Fragment>
        ),
      },
      {
        header: t('columns.exact_consecutive'),
        valueGetter: (item) => (
          <Fragment>
            {renderNumber(
              item.exactConsecutiveCancellations,
              t('fields.cancel'),
            )}
            {renderNumber(item.exactConsecutiveDisputes, t('fields.dispute'))}
            {renderNumber(
              item.exactConsecutiveManualComplete,
              t('fields.manual_complete'),
            )}
          </Fragment>
        ),
      },
    ],
    [t, renderNumber],
  );

  const filtersDefinitions = useMemo<
    FilterDefinition<LimitsUpdatesHistoryEntity>[]
  >(
    () => [
      {
        label: tCommon('filters.requisites_id'),
        name: 'requisitesId',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: tCommon('filters.order_id'),
        name: 'orderId',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
    ],
    [tCommon],
  );

  return (
    <CrudPage
      table={{ columns, queryResult, paginated: true }}
      filters={{ filtersDefinitions, withCommon: true, commonDateTime: true }}
    />
  );
};
